﻿// Company names and sectors for any US ticker, read from your database.
// getCompany(ticker) returns { ticker, name, sector, industry, exchange, category }
// or undefined while it is loading. useCompanyInfoVersion() re-renders the page
// when new names arrive.
import { useSyncExternalStore } from "react";
import { API_URL } from "./config.js";

const store = new Map();   // ticker -> company, or null when unknown
const pending = new Set();
const listeners = new Set();
let version = 0;
let timer = null;

const bump = () => {
  version += 1;
  listeners.forEach((l) => l());
};
const shape = (r) => ({ ...r, category: r.sector || r.industry || "Other" });

async function flush() {
  timer = null;
  const batch = [...pending];
  pending.clear();
  for (let i = 0; i < batch.length; i += 100) {
    const chunk = batch.slice(i, i + 100);
    try {
      const res = await fetch(`${API_URL}/companies?tickers=${encodeURIComponent(chunk.join(","))}`);
      const rows = res.ok ? await res.json() : [];
      chunk.forEach((t) => store.set(t, null));
      rows.forEach((r) => store.set(r.ticker, shape(r)));
    } catch {
      chunk.forEach((t) => store.set(t, null));
    }
  }
  bump();
}

export function getCompany(ticker) {
  const t = String(ticker || "").toUpperCase();
  if (store.has(t)) return store.get(t) || undefined;
  if (!pending.has(t)) {
    pending.add(t);
    if (!timer) timer = setTimeout(flush, 20);
  }
  return undefined;
}

/** Store companies we already have (e.g. search results) so they show up instantly. */
export function rememberCompanies(rows) {
  rows.forEach((r) => store.set(r.ticker, shape(r)));
  bump();
}

export function useCompanyInfoVersion() {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => version
  );
}
